import API from './api';
import Reports from './reports';
import { IN_OPERATOR, NOT_IN_OPERATOR } from '../utils/reportsConsts';

const api = new API();

const URL_SDK_INTEGRATED_PUBLIC_IDS = '/apps/get_sdk_integrated_public_ids';

export const SITE_PUBLIC_ID_DIMENSION = 'site_public_id';

export const ERROR_NO_SDK_INTEGRATED_SITES = 'ERROR_NO_SDK_INTEGRATED_SITES';

// Fresh Error per throw (accurate stack); the stable `code` lets the saga match without the message.
export const createNoSdkIntegratedSitesError = () => {
    const error = new Error('retention has no SDK-integrated app sites to report on');
    error.code = ERROR_NO_SDK_INTEGRATED_SITES;
    return error;
};

export const intersectRetentionSiteFilters = (filters, sdkPublicIds) => {
    const userSiteFilters = (filters || []).filter(
        filter =>
            filter.dimension === SITE_PUBLIC_ID_DIMENSION && [IN_OPERATOR, NOT_IN_OPERATOR].includes(filter.operator)
    );

    return userSiteFilters.reduce((publicIds, filter) => {
        const filterIds = new Set(filter.values || []);
        return publicIds.filter(publicId =>
            filter.operator === IN_OPERATOR ? filterIds.has(publicId) : !filterIds.has(publicId)
        );
    }, sdkPublicIds);
};

// Retention runs on the shared reporting endpoint but is always scoped to SDK-integrated sites.
// The site filter is injected here (not in the query selectors) so it never leaks into the URL.
class Retention {
    constructor() {
        this.reportsApi = new Reports();
        this.sdkIntegratedPublicIdsPromise = null;
        // The saga layer invokes runQuery detached (redux-saga call effect), so keep it bound.
        this.runQuery = this.runQuery.bind(this);
    }

    getSdkIntegratedPublicIds() {
        if (!this.sdkIntegratedPublicIdsPromise) {
            this.sdkIntegratedPublicIdsPromise = api
                .getJson(URL_SDK_INTEGRATED_PUBLIC_IDS, {
                    method: 'GET',
                    credentials: 'include',
                    cache: false,
                    unique: true,
                })
                .then(response => (Array.isArray(response.value) ? response.value : []))
                .finally(() => {
                    this.sdkIntegratedPublicIdsPromise = null;
                });
        }

        return this.sdkIntegratedPublicIdsPromise;
    }

    async runQuery(params, options) {
        const publicIds = await this.getSdkIntegratedPublicIds();
        const effectivePublicIds = intersectRetentionSiteFilters(params.filters, publicIds);

        if (!effectivePublicIds.length) {
            throw createNoSdkIntegratedSitesError();
        }

        const query = {
            ...params,
            filters: [
                ...(params.filters || []).filter(filter => filter.dimension !== SITE_PUBLIC_ID_DIMENSION),
                {
                    dimension: SITE_PUBLIC_ID_DIMENSION,
                    operator: IN_OPERATOR,
                    values: effectivePublicIds,
                    options: { no_lowercase: true },
                },
            ],
        };

        return this.reportsApi.runQuery(query, options);
    }
}

export default Retention;
